import { Routes } from '@angular/router';
import { DashboardContainerComponent } from './pages/portal-container/dashboard-container/dashboard-container.component';
import { CourseDetailComponent } from './pages/portal-container/course-detail/course-detail.component';
import { RouteAwarePortalDialogComponent } from './pages/portal-container/route-aware-portal-dialog/route-aware-portal-dialog.component';
import { MsalGuard } from '@azure/msal-angular';
import { userDataResolver } from './core/resolvers/user-data.resolver';
import { CustomerOverviewComponent } from './pages/sep-admin-container/customer-overview/customer-overview.component';
import { LearningPlanDetailComponent } from './pages/portal-container/learning-plan-detail/learning-plan-detail.component';
import { roleGuard } from './core/guards/role-guard';
import { checkoutGuard } from './core/guards/checkout-guard';
import { detailsPageGuard } from './core/guards/details-page-guard';
import { LearningPlanBuilderComponent } from './pages/portal-container/learning-plan-builder/learning-plan-builder.component';
import { activationResolver } from './domains/payment/product/resolvers/activation.resolver';
import { CourseLibraryComponent } from './pages/portal-container/course-library/course-library.component';
import { TeamContainerComponent } from './pages/portal-container/team-container/team-container.component';
import { CourseSettingsComponent } from './pages/portal-container/course-settings/course-settings.component';
import { DataProcessingAgreementComponent } from './domains/organization/organization/components/data-processing-agreement/data-processing-agreement.component';
import { SubscriptionSettingsComponent } from './domains/payment/subscription/components/subscription-settings/subscription-settings.component';
import { billingResolver } from './core/resolvers/billing.resolver';
import { checkBillinGuard } from './core/guards/billing.guard';

function getPortalRouteChildren(): Routes {
  return [
    {
      path: 'dashboard',
      component: DashboardContainerComponent,
    },
    {
      path: 'courses/:id',
      component: CourseDetailComponent,
      children: [
        {
          path: 'dialog',
          component: RouteAwarePortalDialogComponent,
        },
      ],
      canActivate: [detailsPageGuard],
    },
    {
      path: 'learningPlans/builder',
      component: LearningPlanBuilderComponent,
      canActivate: [detailsPageGuard],
    },
    {
      path: 'learningPlans/:id',
      canActivate: [detailsPageGuard],
      children: [
        {
          path: '',
          component: LearningPlanDetailComponent,
          children: [
            {
              path: 'dialog',
              component: RouteAwarePortalDialogComponent,
            },
          ],
        },
        {
          path: 'edit',
          component: LearningPlanBuilderComponent,
        },
      ],
    },
    {
      path: 'dataProcessing',
      component: DataProcessingAgreementComponent,
    },
    {
      path: 'billing',
      component: SubscriptionSettingsComponent,
      resolve: { billingInformation: billingResolver },
      canActivate: [checkBillinGuard],
    },
    {
      path: 'courseLibrary',
      component: CourseLibraryComponent,
    },
    {
      path: 'courseSettings',
      component: CourseSettingsComponent,
      canActivate: [detailsPageGuard],
    },
    {
      path: 'team',
      component: TeamContainerComponent,
      children: [
        {
          path: 'dialog',
          component: RouteAwarePortalDialogComponent,
        },
      ],
    },
    {
      path: '',
      pathMatch: 'full',
      redirectTo: 'dashboard',
    },
    {
      path: '**',
      redirectTo: 'dashboard',
    },
  ];
}

function getAdminRouteChildren(): Routes {
  return [
    {
      path: 'customers',
      component: CustomerOverviewComponent,
    },
  ];
}

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'portal',
  },
  {
    path: 'register',
    canActivate: [checkoutGuard],
    loadComponent: async () =>
      import('./pages/registration-container/registration-container.component').then(
        (c) => c.RegistrationContainerComponent,
      ),
  },
  {
    path: 'register-v2',
    canActivate: [checkoutGuard],
    loadComponent: async () =>
      import('./pages/registration-container-v2/registration-container-v2.component').then(
        (c) => c.RegistrationContainerV2Component,
      ),
  },
  {
    path: 'addPaymentMethod',
    canActivate: [MsalGuard, detailsPageGuard],
    resolve: {
      data: activationResolver,
    },
    loadComponent: async () =>
      import('./pages/registration-container/add-payment-method/add-payment-method.component').then(
        (c) => c.AddPaymentMethodComponent,
      ),
  },
  {
    path: 'reactivate',
    canActivate: [MsalGuard, detailsPageGuard],
    resolve: {
      data: activationResolver,
    },
    loadComponent: async () =>
      import('./pages/registration-container/add-payment-method/add-payment-method.component').then(
        (c) => c.AddPaymentMethodComponent,
      ),
  },

  {
    path: 'portal',
    loadComponent: async () =>
      import('./pages/portal-container/portal-container.component').then(
        (c) => c.PortalContainerComponent,
      ),
    canActivate: [MsalGuard],
    resolve: {
      userData: userDataResolver,
    },
    children: getPortalRouteChildren(),
  },
  {
    path: 'admin',
    loadComponent: async () =>
      import('./pages/sep-admin-container/sep-admin-container.component').then(
        (c) => c.SepAdminContainerComponent,
      ),
    resolve: {
      userData: userDataResolver,
    },
    canActivate: [MsalGuard, roleGuard],
    children: getAdminRouteChildren(),
  },
  {
    path: '**',
    component: DashboardContainerComponent,
  },
];
